import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { ClassificationView } from '../..//common/ui/classification-view';
import { colorsConfig, disabledColorConfig, nameMaxLength } from '../../common/constants';
import { ClassificationIcon } from '../../common/ui/classification-icon';
import { messages } from '../../messages';

import { type DataClassificationLevelProps } from './types';

const styles = cssMap({
	base: {
		display: 'inline-flex',
		paddingTop: token('space.050', '4px'),
		paddingRight: token('space.050', '4px'),
		paddingBottom: token('space.050', '4px'),
		paddingLeft: token('space.050', '4px'),
		gap: token('space.050', '4px'),
		alignItems: 'center',
		justifyContent: 'center',
		whiteSpace: 'normal',
		textOverflow: 'ellipsis',
		cursor: 'default',
		borderRadius: token('radius.small'),
	},
	compact: {
		height: '20px',
	},
	expanded: {
		height: '24px',
	},
	disabled: {
		color: token('color.text.disabled', '#091E424F'),
		backgroundColor: token('color.background.disabled', '#091E4208'),
		cursor: 'default',
	},
	clickable: {
		cursor: 'pointer',
	},
	red: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.red.subtler', '#FFD5D2'),
	},
	redClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.red.subtler', '#FFD5D2'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.red.subtler.hovered', '#FD9891'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.red.subtler.pressed', '#F87168'),
		},
	},
	redBold: {
		color: token('color.text.inverse', '#FFFFFF'),
		backgroundColor: token('color.background.accent.red.bolder', '#C9372C'),
	},
	redBoldClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.red.bolder', '#C9372C'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.red.bolder.hovered', '#AE2E24'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.red.bolder.pressed', '#5D1F1A'),
		},
	},
	orange: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.orange.subtle', '#FEA362'),
	},
	orangeClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.orange.subtle', '#FEA362'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.orange.subtle.hovered', '#FEC195'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.orange.subtle.pressed', '#FEDEC8'),
		},
	},
	yellow: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.yellow.subtlest', '#FFF7D6'),
	},
	yellowClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.yellow.subtlest', '#FFF7D6'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.yellow.subtlest.hovered', '#F8E6A0'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.yellow.subtlest.pressed', '#F5CD47'),
		},
	},
	green: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.green.subtlest', '#DCFFF1'),
	},
	greenClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.green.subtlest', '#DCFFF1'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.green.subtlest.hovered', '#BAF3DB'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.green.subtlest.pressed', '#7EE2B8'),
		},
	},
	blue: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.blue.subtler', '#CCE0FF'),
	},
	blueClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.blue.subtler', '#CCE0FF'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtler.hovered', '#85B8FF'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.blue.subtler.pressed', '#579DFF'),
		},
	},
	teal: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.teal.subtler', '#C6EDFB'),
	},
	tealClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.teal.subtler', '#C6EDFB'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.teal.subtler.hovered', '#9DD9EE'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.teal.subtler.pressed', '#6CC3E0'),
		},
	},
	lime: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.lime.subtler', '#D3F1A7'),
	},
	limeClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.lime.subtler', '#D3F1A7'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.lime.subtler.hovered', '#B3DF72'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.lime.subtler.pressed', '#94C748'),
		},
	},
	purple: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.purple.subtlest', '#F3F0FF'),
	},
	purpleClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.purple.subtlest', '#F3F0FF'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered', '#DFD8FD'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.purple.subtlest.pressed', '#B8ACF6'),
		},
	},
	grey: {
		color: token('color.text', '#172B4D'),
		backgroundColor: token('color.background.accent.gray.subtlest', '#F1F2F4'),
	},
	greyClickable: {
		'&:focus-visible': {
			backgroundColor: token('color.background.accent.gray.subtlest', '#F1F2F4'),
		},
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered', '#DCDFE4'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.gray.subtlest.pressed', '#B3B9C4'),
		},
	},
	popup: {
		width: '330px',
	},
});

export default function DataClassificationLevel({
	testId,
	name,
	tooltip,
	tooltipPosition,
	color = 'GREY',
	iconSize = 'small',
	guideline,
	guidelineADF,
	isDisabled = false,
	isPopupDisabled = false,
	isPopupOpenByDefault = false,
	getPopupStateControllers,
	popupZIndex,
	guidelineRepresentation = 'PLAIN',
	onPopupShown,
	onPopupClose,
	onClick,
	renderPopupComponent,
	popupLabel,
	popupTitleId,
	popupRole,
	popupPlacement = 'bottom-start',
	shouldRenderToParent,
	isIconOnly: isIconOnlyProp = false,
	associatedPoliciesCount,
	spacing = 'default',
}: DataClassificationLevelProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isPopupOpen, setIsPopupOpen] = useState(
		isDisabled || isPopupDisabled ? false : isPopupOpenByDefault,
	);
	if (getPopupStateControllers) {
		const getOpenState = () => isPopupOpen;
		const setOpenState = (isOpen: boolean) => setIsPopupOpen(isOpen);
		getPopupStateControllers(getOpenState, setOpenState);
	}

	const isMissingName = !name || name.length === 0;
	const isIconOnly = isMissingName || isIconOnlyProp;

	const nameWithRules = isMissingName
		? ''
		: associatedPoliciesCount
			? name + ' ' + formatMessage(messages.rules, { associatedPoliciesCount })
			: name;
	const nameTruncated = (nameWithRules ?? '').substring(0, nameMaxLength);

	const iconPx: number =
		iconSize === 'small'
			? 12
			: iconSize === 'medium'
				? 18
				: iconSize === 'large'
					? 24
					: iconSize === 'xlarge'
						? 32
						: iconSize === 'xxlarge'
							? 40
							: 12;

	const handleContainerClicked = (): void => {
		if (isPopupDisabled || isDisabled || isMissingName) {
			return;
		}

		if (onPopupShown) {
			onPopupShown();
		}

		setIsPopupOpen(!isPopupOpen);
	};

	const closePopup = () => {
		setIsPopupOpen(false);
	};

	const isClickable = !isPopupDisabled && !isDisabled && !isMissingName;

	const getContainer = (triggerProps = {}) => {
		// @ts-ignore
		const { 'aria-expanded': ariaExpanded, ...restProps } = triggerProps;

		const Container = isClickable ? Pressable : Box;

		return (
			<Container
				role={isClickable ? 'button' : 'presentation'}
				aria-disabled={isDisabled}
				{...(isClickable
					? { 'aria-label': `${formatMessage(messages.dataClassificationLevel)} - ${name}` }
					: {})} // Add aria-label only if clickable as aria-labeledby is not sufficient when popup is collapsed
				xcss={cx(
					...[
						styles.base,
						// Add size styles if not icon only
						...(!isIconOnly ? [spacing === 'expanded' ? styles.expanded : styles.compact] : []),
						// Add disabled styles if disabled
						...(isDisabled ? [styles.disabled] : []),
						// Add color styles
						...(!isDisabled
							? [
									{
										RED: styles.red,
										RED_BOLD: styles.redBold,
										ORANGE: styles.orange,
										YELLOW: styles.yellow,
										GREEN: styles.green,
										BLUE: styles.blue,
										TEAL: styles.teal,
										LIME: styles.lime,
										PURPLE: styles.purple,
										GREY: styles.grey,
									}[color],
								]
							: []),
						// Add clickable styles if clickable
						...(!isDisabled && isClickable
							? [
									styles.clickable,
									{
										RED: styles.redClickable,
										RED_BOLD: styles.redBoldClickable,
										ORANGE: styles.orangeClickable,
										YELLOW: styles.yellowClickable,
										GREEN: styles.greenClickable,
										BLUE: styles.blueClickable,
										TEAL: styles.tealClickable,
										LIME: styles.limeClickable,
										PURPLE: styles.purpleClickable,
										GREY: styles.greyClickable,
									}[color],
								]
							: []),
					],
				)}
				testId={testId}
				padding="space.050"
				onClick={() => {
					if (onClick) {
						onClick();
					}
					handleContainerClicked();
				}}
				{...(isClickable ? triggerProps : restProps)}
			>
				<Flex justifyContent="center" alignItems="center">
					<ClassificationIcon
						width={iconPx}
						height={iconPx}
						color={isDisabled ? disabledColorConfig.iconColor : colorsConfig[color].iconColor}
					/>
				</Flex>
				{!isIconOnly && (
					<Text
						size="small"
						maxLines={1}
						color={color === 'RED_BOLD' ? 'color.text.inverse' : 'color.text'}
						testId="data-classification-level-name"
					>
						{nameTruncated}
					</Text>
				)}
			</Container>
		);
	};

	return isPopupDisabled ? (
		getContainer()
	) : (
		// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
		<Popup
			label={popupLabel}
			titleId={popupTitleId}
			role={popupRole}
			shouldRenderToParent={shouldRenderToParent}
			placement={popupPlacement}
			trigger={(triggerProps) => (
				<Tooltip
					content={isPopupOpen ? null : tooltip}
					hideTooltipOnClick
					position={tooltipPosition}
				>
					{getContainer(triggerProps)}
				</Tooltip>
			)}
			content={() =>
				renderPopupComponent ? (
					renderPopupComponent(closePopup)
				) : (
					<Box
						padding="space.250"
						xcss={styles.popup}
						role="dialog"
						aria-labelledby="data-classification-read-content-name"
						aria-describedby="data-classification-read-content-guideline"
						aria-modal="true"
					>
						<ClassificationView
							name={name}
							guideline={guideline}
							guidelineADF={guidelineADF}
							guidelineRepresentation={guidelineRepresentation}
						/>
					</Box>
				)
			}
			isOpen={isPopupOpen}
			onClose={() => {
				if (onPopupClose) {
					onPopupClose();
				}
				closePopup();
			}}
			zIndex={popupZIndex}
		/>
	);
}
